import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Bell, CheckCircle2, XCircle, Clock, Info } from 'lucide-react';
import Modal from '../components/Modal';

const Notifications = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/invitations');
      setInvitations(data);
    } catch (err) {
      console.error("Erro ao carregar convites");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (inviteId) => {
    try {
      await api.post(`/invitations/${inviteId}/accept`);
      setInvitations(prev => prev.filter(inv => inv.id !== inviteId));
      setModalConfig({ isOpen: true, title: 'Convite aceite', message: 'O projeto já está disponível na tua dashboard.', type: 'success' });
    } catch (err) {
      setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível aceitar o convite.', type: 'danger' });
    }
  };

  const handleReject = (inviteId) => {
    setModalConfig({
      isOpen: true,
      title: 'Rejeitar convite',
      message: 'Tens a certeza que desejas rejeitar este convite?',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.post(`/invitations/${inviteId}/reject`);
          setInvitations(prev => prev.filter(inv => inv.id !== inviteId));
          setModalConfig({ isOpen: true, title: 'Convite rejeitado', message: 'O convite foi removido.', type: 'info' });
        } catch (err) {
          setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível rejeitar o convite.', type: 'danger' });
        }
      }
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 py-10 animate-in fade-in duration-300">
      <Modal {...modalConfig} onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} cancelText="Fechar" />

      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Notificações</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Gere os teus convites de acesso a projetos.</p>
        </div>
        <div className="bg-blue-600/10 p-3 rounded-2xl text-blue-600 shrink-0">
          <Bell size={24} />
        </div>
      </div>

      {invitations.length === 0 && !loading ? (
        <div className="bg-slate-50 dark:bg-slate-800/40 border border-dashed border-slate-200 dark:border-slate-700 p-20 rounded-3xl text-center">
          <CheckCircle2 className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={40} />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Estás atualizado.</p>
          <p className="text-slate-400 text-sm mt-1">Não existem convites pendentes de momento.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {invitations.map(inv => (
            <div key={inv.id} className="bg-white dark:bg-slate-800 p-6 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-5 hover:border-blue-400/60 transition-all">
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-12 h-12 bg-blue-600/10 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
                  <Clock size={22} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">{inv.project_name}</h3>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                      {inv.access_level}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Convidado por <span className="font-medium text-slate-700 dark:text-slate-200">{inv.inviter_email}</span>.</p>
                  <div className="flex items-center mt-2 text-xs text-slate-400 gap-1.5">
                    <Info size={13} />
                    <span>Ao aceitar, o projeto fica visível na tua dashboard.</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 w-full md:w-auto shrink-0">
                <button
                  onClick={() => handleAccept(inv.id)}
                  className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-all active:scale-[0.98]"
                >
                  <CheckCircle2 size={18} />
                  <span>Aceitar</span>
                </button>
                <button
                  onClick={() => handleReject(inv.id)}
                  className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                  title="Rejeitar convite"
                >
                  <XCircle size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          {[1, 2].map(i => <div key={i} className="h-28 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-3xl"></div>)}
        </div>
      )}
    </div>
  );
};

export default Notifications;
